import React, { useEffect, useMemo, useState } from "react";
import { Alert, Box, Button, Typography } from "@mui/material";
import { useSelector } from "react-redux";
import { usePlaceholderLandlordPhoneMatch } from "services/hooks/rentals";
import AnonymousLandlordPhoneMatchTable, {
  getPhoneMatchRegisteredLandlordId,
  getPhoneMatchRowKey,
} from "components/Tables/AnonymousLandlordPhoneMatchTable";

const ATTACH_ERROR_LABELS = {
  occupancy_not_found: "Occupancy not found",
  not_anonymous_placeholder: "Rental is no longer an anonymous placeholder",
  phone_mismatch: "Phone does not match this landlord",
};

const AnonymousLandlords = () => {
  const roleFromState = useSelector((state) => state.role);
  const isAdminFlag = useSelector((state) => state.isAdmin);
  const role = roleFromState || (isAdminFlag === 1 ? "admin" : "manager");
  const isManager = role === "manager";

  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [actionMessage, setActionMessage] = useState({ type: "", text: "" });
  const [lastAttachResult, setLastAttachResult] = useState(null);

  const {
    candidates,
    isLoadingCandidates,
    candidatesError,
    refetchCandidates,
    isAttachingOccupancies,
    attachError,
    attachPlaceholderOccupancies,
  } = usePlaceholderLandlordPhoneMatch();

  const rowKeysOnPage = useMemo(() => candidates.map((row) => getPhoneMatchRowKey(row)), [candidates]);

  const selectableRowKeysOnPage = useMemo(
    () =>
      candidates
        .filter((row) => getPhoneMatchRegisteredLandlordId(row) != null)
        .map((row) => getPhoneMatchRowKey(row)),
    [candidates]
  );

  useEffect(() => {
    setSelectedRowKeys((current) => current.filter((key) => rowKeysOnPage.includes(key)));
  }, [rowKeysOnPage]);

  const handleToggleRow = (row) => {
    if (getPhoneMatchRegisteredLandlordId(row) == null) return;
    const key = getPhoneMatchRowKey(row);
    setSelectedRowKeys((current) =>
      current.includes(key) ? current.filter((k) => k !== key) : [...current, key]
    );
  };

  const handleToggleAll = () => {
    const allSelected =
      selectableRowKeysOnPage.length > 0 &&
      selectableRowKeysOnPage.every((key) => selectedRowKeys.includes(key));
    if (allSelected) {
      setSelectedRowKeys((current) => current.filter((key) => !selectableRowKeysOnPage.includes(key)));
    } else {
      setSelectedRowKeys((current) => Array.from(new Set([...current, ...selectableRowKeysOnPage])));
    }
  };

  const selectedRows = useMemo(
    () => candidates.filter((row) => selectedRowKeys.includes(getPhoneMatchRowKey(row))),
    [candidates, selectedRowKeys]
  );

  const handleAttach = async () => {
    setActionMessage({ type: "", text: "" });
    setLastAttachResult(null);

    if (selectedRows.length === 0) {
      setActionMessage({ type: "error", text: "Select at least one row to attach." });
      return;
    }

    const missingLandlord = selectedRows.filter((row) => getPhoneMatchRegisteredLandlordId(row) == null);
    if (missingLandlord.length > 0) {
      setActionMessage({
        type: "error",
        text: "Some selected rows are missing a matched registered landlord id from the server. Refresh or contact support.",
      });
      return;
    }

    const confirmed = window.confirm(
      `Attach ${selectedRows.length} row(s) to the indicated registered landlord(s)?`
    );
    if (!confirmed) return;

    const byLandlord = new Map();
    for (const row of selectedRows) {
      const landlordId = getPhoneMatchRegisteredLandlordId(row);
      const occId = row.occupancy_id;
      if (occId == null || landlordId == null) continue;
      if (!byLandlord.has(landlordId)) byLandlord.set(landlordId, []);
      const list = byLandlord.get(landlordId);
      if (!list.includes(occId)) list.push(occId);
    }

    const mergedAttached = [];
    const mergedErrors = [];
    let hadRequestFailure = false;
    let failureMessage = "";

    for (const [landlordId, occupancyIds] of byLandlord) {
      const result = await attachPlaceholderOccupancies(landlordId, occupancyIds);
      if (result.success && result.data) {
        if (Array.isArray(result.data.attached)) mergedAttached.push(...result.data.attached);
        if (Array.isArray(result.data.errors)) mergedErrors.push(...result.data.errors);
      } else {
        hadRequestFailure = true;
        failureMessage = result.message || "Attach failed.";
        break;
      }
    }

    if (hadRequestFailure) {
      setActionMessage({ type: "error", text: failureMessage });
      return;
    }

    setLastAttachResult({ attached: mergedAttached, errors: mergedErrors });
    const attachedCount = mergedAttached.length;
    const errorCount = mergedErrors.length;
    setActionMessage({
      type: errorCount > 0 ? "warning" : "success",
      text:
        errorCount > 0
          ? `Attached ${attachedCount}; ${errorCount} row(s) reported errors (see below).`
          : `Successfully attached ${attachedCount} occupancy link(s).`,
    });
    setSelectedRowKeys([]);
    refetchCandidates();
  };

  if (!isManager) {
    return (
      <Box m={2}>
        <Alert severity="warning">Only Property Managers can access Anonymous Landlords.</Alert>
      </Box>
    );
  }

  return (
    <Box m="20px">
      <Typography variant="h4" sx={{ fontWeight: "bold", mb: 1 }}>
        Anonymous Landlords
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Phone-match pairs between WhatsApp-onboard placeholder rentals and registered landlords (same last-9 phone
        rules as onboarding). Select rows and attach to promote the rental to the matched landlord.
      </Typography>
      <Typography variant="body2" sx={{ mb: 2 }}>
        Showing {candidates.length} candidate row(s). List loads automatically; use Refresh if you need the latest.
      </Typography>

      {actionMessage.text && (
        <Alert severity={actionMessage.type === "error" ? "error" : actionMessage.type} sx={{ mb: 2 }}>
          {actionMessage.text}
        </Alert>
      )}
      {attachError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {attachError}
        </Alert>
      )}

      <Box sx={{ mb: 2 }}>
        <Button
          variant="contained"
          color="success"
          onClick={handleAttach}
          disabled={
            selectedRowKeys.length === 0 ||
            isAttachingOccupancies ||
            (isLoadingCandidates && candidates.length === 0)
          }
        >
          {isAttachingOccupancies
            ? "Attaching…"
            : `Attach selected (${selectedRowKeys.length})`}
        </Button>
      </Box>

      {lastAttachResult?.attached?.length > 0 && (
        <Alert severity="success" sx={{ mb: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
            Attached
          </Typography>
          {lastAttachResult.attached.map((row) => (
            <Typography key={`${row.occupancy_id}-${row.landlord_id}`} variant="body2">
              Occupancy {row.occupancy_id}: {row.previous_landlord_name} → {row.landlord_name} (unit {row.unit_id})
            </Typography>
          ))}
        </Alert>
      )}

      {lastAttachResult?.errors?.length > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
            Not attached
          </Typography>
          {lastAttachResult.errors.map((row, index) => (
            <Typography key={`${row.occupancy_id}-${row.error}-${index}`} variant="body2">
              Occupancy {row.occupancy_id}: {ATTACH_ERROR_LABELS[row.error] || row.error}
            </Typography>
          ))}
        </Alert>
      )}

      <AnonymousLandlordPhoneMatchTable
        data={candidates}
        loading={isLoadingCandidates && candidates.length === 0}
        error={candidatesError}
        selectedRowKeys={selectedRowKeys}
        onToggleRow={handleToggleRow}
        onToggleAll={handleToggleAll}
        onRefresh={() => refetchCandidates()}
        isRefreshing={isLoadingCandidates && candidates.length > 0}
      />
    </Box>
  );
};

export default AnonymousLandlords;
